import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { faqPageJson } from "@/lib/faq-schema";
import { ORG_REF, PHYSICIAN_REF } from "@/lib/medical-schema";
import { PageSection } from "@/components/site/PageSection";
import { useHashFocus } from "@/lib/anchor-focus";
import { canonicalAndAlternates } from "@/lib/hreflang";


import { faqAnchorId } from "@/components/site/InsuranceProse";
import { schemaDateFields } from "@/lib/schema-dates";

const CANONICAL = "https://virginiabariatric.com/resources/qualifications";

const QUAL_IMAGE = {
  url: "https://virginiabariatric.com/__l5e/assets-v1/9df2b235-a65d-4916-a795-902d779d12d9/og-qualifications.png",
  alt: "Bariatric surgery requirements: BMI, conditions, insurance, and age criteria from Virginia Bariatric Surgery",
  width: 1200,
  height: 630,
};

const FAQ_ITEMS = [
  {
    question: "Do I qualify for weight loss surgery?",
    answer:
      "You likely qualify if your BMI is 40 or higher, or 35 or higher with an obesity-related condition such as type 2 diabetes, high blood pressure, or sleep apnea. Self-pay patients qualify at a BMI of 35 or higher on weight alone, or 30 to 34.9 with a qualifying medical condition. The fastest way to confirm is a consultation where we review your BMI, health history, and insurance plan.",
  },
  {
    question: "Which medical conditions count toward qualifying?",
    answer:
      "If your BMI is 35 to 39.9, your insurance company will not cover surgery unless you have a qualifying obesity-related condition. Your insurer decides which conditions are qualifying. The rules may differ from insurer to insurer, but some things are constant. Four conditions always qualify you for surgery: type 2 diabetes, high blood pressure, obstructive sleep apnea, and coronary artery disease or other heart disease. Additional obesity-related health issues may qualify you, but they may not: high cholesterol or other lipid disorder, severe osteoarthritis or weight-bearing joint disease, fatty liver disease, severe gastroesophageal reflux, and pulmonary hypertension or obesity hypoventilation syndrome. An easy way to check is by googling, \"Medical policy for weight loss surgery for [my insurance company]\". Another easy way to check is by calling us. We'll find out for you.\n\nYou may qualify without knowing it: studies have shown that over 70% of people with a BMI between 35 and 39.9 have obstructive sleep apnea, and many have not been evaluated.",
  },
  {
    question: "Why are the BMI thresholds different if I have insurance?",
    answer:
      "Most insurance criteria are based on a 1991 National Institutes of Health consensus statement: BMI 40 and above, or 35 and above with a comorbidity. In 2022, the American Society for Metabolic and Bariatric Surgery and the International Federation for the Surgery of Obesity issued updated indications recommending surgery for anyone with a BMI of 35 or higher regardless of related conditions, and for people with metabolic disease at a BMI of 30 to 34.9. When you are self-paying, current clinical guidelines govern; when insurance is paying, we have to go by your plan's medical policy.",
  },
  {
    question: "Are the BMI thresholds different for patients of East Asian descent?",
    answer:
      "Yes. At any given BMI, people of East Asian descent tend to have more visceral fat and a higher risk of type 2 diabetes and cardiovascular disease. The 2022 ASMBS and IFSO guidelines recommend adjusting BMI risk zones to define obesity at a threshold of 25 to 27.5 in this population. In practice, a patient of East Asian descent at a BMI of 32 may be an entirely appropriate surgical candidate. Many insurers' medical policies recognize an Asian-heritage adjustment, but some do not. Checking is easy, and it should happen early to avoid a disappointing surprise later on.",
  },
  {
    question: "Do I qualify for endoscopic sleeve gastroplasty (ESG)?",
    answer:
      "ESG is FDA-authorized for adults with a BMI of 30 to 50 who have not been able to lose weight or maintain weight loss through more conservative measures. It is almost always self-pay. Unlike sleeve gastrectomy and gastric bypass, which have a floor but no upper BMI limit, ESG has both a lower and an upper bound.",
  },
  {
    question: "Do I qualify for the intragastric balloon?",
    answer:
      "The gastric balloon (Orbera) is FDA-approved for a BMI of 30 to 40, with prior conservative weight-loss attempts and participation in a supervised diet and behavior program. The balloon stays in place for six months. It is generally not covered by insurance and is almost always self-pay.",
  },
] as const;

export const Route = createFileRoute("/resources/qualifications/")({
  head: () => ({
    meta: [
      { title: "Do You Qualify for Bariatric Surgery? | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Bariatric surgery BMI requirements in plain language: insurance vs. self-pay thresholds, qualifying conditions, East Asian adjustments, and ESG and balloon BMI windows.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Do You Qualify for Bariatric Surgery? | Virginia Bariatric" },
        { property: "og:description", content: "Bariatric surgery BMI requirements in plain language: insurance vs. self-pay thresholds, qualifying conditions, East Asian adjustments, and ESG and balloon BMI windows." },
        { property: "og:type", content: "article" },
        { property: "og:url", content: CANONICAL },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/9df2b235-a65d-4916-a795-902d779d12d9/og-qualifications.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:alt", content: "Bariatric surgery requirements: BMI, conditions, insurance, and age criteria from Virginia Bariatric Surgery" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/9df2b235-a65d-4916-a795-902d779d12d9/og-qualifications.png" },
        { name: "twitter:image:alt", content: "Bariatric surgery qualifications: BMI, conditions, insurance, and age criteria from Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "Do You Qualify for Bariatric Surgery? | Virginia Bariatric" },
        { name: "twitter:description", content: "Bariatric surgery BMI requirements in plain language: insurance vs. self-pay thresholds, qualifying conditions, East Asian adjustments, and ESG and balloon BMI windows." },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/resources/qualifications") : [])],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MedicalCondition",
              "@id": "https://virginiabariatric.com/#condition-obesity",
              name: "Obesity",
              alternateName: ["Severe obesity", "Class II obesity", "Class III obesity"],
              code: {
                "@type": "MedicalCode",
                code: "E66",
                codingSystem: "ICD-10",
              },
              possibleTreatment: [
                { "@type": "MedicalTherapy", name: "Sleeve gastrectomy" },
                { "@type": "MedicalTherapy", name: "Roux-en-Y gastric bypass" },
                { "@type": "MedicalTherapy", name: "Endoscopic sleeve gastroplasty" },
                { "@type": "MedicalTherapy", name: "Intragastric balloon (Orbera)" },
                { "@type": "MedicalTherapy", name: "GLP-1 weight-loss medication" },
              ],
              associatedAnatomy: { "@type": "AnatomicalStructure", name: "Stomach" },
              relevantSpecialty: {
                "@type": "MedicalSpecialty",
                name: "Bariatric",
              },
            },
            {
              "@type": "ImageObject",
              "@id": `${CANONICAL}#primaryimage`,
              url: QUAL_IMAGE.url,
              contentUrl: QUAL_IMAGE.url,
              caption: QUAL_IMAGE.alt,
              width: QUAL_IMAGE.width,
              height: QUAL_IMAGE.height,
            },
            {
              "@type": "MedicalWebPage",
              "@id": `${CANONICAL}#webpage`,
              url: CANONICAL,
              name: "Do You Qualify for Bariatric Surgery?",
              description:
                "Bariatric surgery qualifications in Northern Virginia: BMI thresholds by payor, qualifying obesity-related conditions, East Asian adjustments, and ESG and balloon BMI windows.",
              inLanguage: "en-US",
              isPartOf: { "@id": "https://virginiabariatric.com/#website" },
              about: { "@id": "https://virginiabariatric.com/#condition-obesity" },
              mainContentOfPage: {
                "@type": "WebPageElement",
                cssSelector: "main",
              },
              primaryImageOfPage: { "@id": `${CANONICAL}#primaryimage` },
              image: { "@id": `${CANONICAL}#primaryimage` },
              mainEntity: { "@id": `${CANONICAL}#faq` },
              significantLink: [`${CANONICAL}#faq`, `${CANONICAL}#bmi-and-payor`],
              audience: {
                "@type": "MedicalAudience",
                audienceType: "Patient",
                geographicArea: {
                  "@type": "AdministrativeArea",
                  name: "Northern Virginia",
                },
              },
              // MedicalSpecialty is an enumeration; "Bariatric" is not a member.
              specialty: "https://schema.org/Surgical",
              ...schemaDateFields("/resources/qualifications"),
              reviewedBy: PHYSICIAN_REF,
              author: PHYSICIAN_REF,
              publisher: ORG_REF,
              breadcrumb: { "@id": `${CANONICAL}#breadcrumb` },
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: ["h1", "h2"],
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${CANONICAL}#faq`,
              url: CANONICAL,
              inLanguage: "en-US",
              isPartOf: { "@id": `${CANONICAL}#webpage` },
              mainEntityOfPage: { "@id": `${CANONICAL}#webpage` },
              publisher: ORG_REF,
              mainEntity: FAQ_ITEMS.map((f) => {
                const anchor = `${CANONICAL}#${faqAnchorId(f.question)}`;
                return {
                  "@type": "Question",
                  "@id": anchor,
                  url: anchor,
                  name: f.question,
                  inLanguage: "en-US",
                  acceptedAnswer: { "@type": "Answer", text: f.answer, url: anchor },
                };
              }),
            },
            {
              "@type": "HowTo",
              name: "How to Find Out Whether You Qualify for Bariatric Surgery",
              description:
                "The four steps to confirm your bariatric surgery candidacy at Virginia Bariatric Surgery in Northern Virginia.",
              url: CANONICAL,
              step: [
                {
                  "@type": "HowToStep",
                  name: "Calculate your BMI",
                  url: `${CANONICAL}#what-to-do-next`,
                  text: "Calculate your body mass index. It takes ten seconds and tells you which eligibility row you are in.",
                },
                {
                  "@type": "HowToStep",
                  name: "Find out whether you have a qualifying condition",
                  url: `${CANONICAL}#what-to-do-next`,
                  text: "If you have not been evaluated for sleep apnea, diabetes, or fatty liver, you may have a qualifying condition and not know it.",
                },
                {
                  "@type": "HowToStep",
                  name: "Call your insurer",
                  url: `${CANONICAL}#what-to-do-next`,
                  text: "Ask two questions: does my plan include a bariatric surgery benefit, and what BMI and documentation does it require.",
                },
                {
                  "@type": "HowToStep",
                  name: "Come in for a consultation",
                  url: `${CANONICAL}#what-to-do-next`,
                  text: "We will confirm where you stand, order what is missing, and give you a straight answer about which options are actually open to you.",
                },
              ],
            },
          ],
        }),
      },
    ] : [],
  }),
  component: QualificationsPage,
});

function QualificationsPage() {
  useHashFocus();
  return (

    <>
      <PageSection className="pt-6 pb-3 text-center">
        <nav aria-label="Breadcrumb" className="mb-3 text-sm text-muted-foreground">
          <ol className="flex flex-wrap justify-center gap-x-2 gap-y-1">
            <li>
              <Link to="/" className="hover:text-brand-mid hover:underline">Home</Link>
              <span aria-hidden="true" className="ml-2">/</span>
            </li>
            <li>
              <Link to="/resources" className="hover:text-brand-mid hover:underline">Resources</Link>
              <span aria-hidden="true" className="ml-2">/</span>
            </li>
            <li aria-current="page" className="text-foreground">Do You Qualify for Bariatric Surgery?</li>
          </ol>
        </nav>
        <h1 className="text-3xl sm:text-4xl md:text-[2.5rem] font-serif text-brand-mid leading-tight">
          Do You Qualify for Bariatric Surgery?
        </h1>

        <p className="mt-2 text-base md:text-lg text-muted-foreground italic">
          It depends on your medical status, your BMI, and whether you plan to use insurance.
        </p>
        <p className="mt-1 text-sm text-muted-foreground">Last reviewed August 18, 2026.</p>
      </PageSection>

      <PageSection maxWidth="4xl" className="pb-8">
        <nav
          aria-label="On this page"
          className="mb-5 rounded-lg border border-border bg-muted/40 p-4 md:p-5"
        >
          <p className="font-serif text-lg text-brand-mid mb-2">On this page</p>
          <ul className="list-disc pl-6 space-y-1.5 text-base">
            <li><a href="#age-and-fitness" className="text-brand-mid underline">Age and medical fitness</a></li>
            <li><a href="#bmi-and-payor" className="text-brand-mid underline">BMI, medical disease, and who is paying</a></li>
            <li><a href="#which-conditions-count" className="text-brand-mid underline">Which conditions count</a></li>
            <li><a href="#why-thresholds-differ" className="text-brand-mid underline">Why the BMI thresholds are different if you have insurance</a></li>
            <li><a href="#east-asian" className="text-brand-mid underline">Caveat 1: East Asian descent</a></li>
            <li><a href="#endoscopic" className="text-brand-mid underline">Caveat 2: the transoral procedures</a></li>
            <li><a href="#what-to-do-next" className="text-brand-mid underline">What to do next</a></li>
            <li><a href="#sources" className="text-brand-mid underline">Sources</a></li>
            <li><a href="#faq" className="text-brand-mid underline">Frequently asked questions</a></li>
          </ul>
        </nav>

        <article className="leading-[1.6] md:text-[1.125rem] space-y-3 md:space-y-3.5">
          <p>
            Most bariatric success stories begin by somebody asking, “Do I qualify
            for weight-loss surgery?”
          </p>
          <p>
            The answer boils down to a few simple questions. The things we
            consider are:
          </p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>
              <strong>Your age</strong> — we offer weight loss surgery to people 18
              and over.
            </li>
            <li>
              <strong>Your medical and emotional fitness</strong> – is it a good time
              for life-changing surgery?
            </li>
            <li>
              <strong>Your weight and height</strong> — these figures tell us your
              body mass index (BMI).
            </li>
            <li>
              <strong>Obesity-related medical diseases</strong> — do you have any?
            </li>
            <li>
              <strong>Who is paying</strong> — are you using insurance or
              self-paying?
            </li>
          </ol>

          <h2 id="age-and-fitness" className="scroll-mt-24 text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            Age and medical fitness
          </h2>
          <p>
            These guidelines apply to everyone, regardless of BMI or payor. They are
            threshold requirements. Meeting them means you can be considered for
            surgery. If you do not meet them, surgery cannot be
            offered—at least until something changes. They include:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>You are 18 or older.</li>
            <li>
              You are healthy enough to undergo surgery and general anesthesia. This
              is assessed in the pre-op evaluation. We might need you to have cardiac
              or pulmonary testing, depending on your history.
            </li>
            <li>
              You are psychologically ready. It is confirmed with an evaluation from
              a qualified behavioral health professional.
            </li>
            <li>You do not have active substance abuse.</li>
            <li>
              You are not pregnant, and you are willing to postpone pregnancy for
              roughly 12 to 18 months after surgery.
            </li>
            <li>You understand the risks.</li>
            <li>
              You are willing to commit to lifelong dietary change, follow-up visits,
              and daily vitamin supplementation.
            </li>
          </ul>
          <p>
            Most people who want surgery can meet those conditions, though
            sometimes there is work to do first.
          </p>
          <p>
            If it turns out that you have work to do, don't be discouraged.
            We'll be with you every step of the way, and we'll help you. You can
            do it! The extra effort will make your eventual success that much
            sweeter.
          </p>
          <div className="mt-8 text-center">
            <Link
              to="/appointments"
              className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-primary px-6 py-3.5 text-base font-medium text-primary-foreground hover:opacity-90"
            >
              Book an Appointment
            </Link>
          </div>

          <h2 id="bmi-and-payor" className="scroll-mt-24 text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            BMI, medical disease, and who is paying
          </h2>
          <p>
            The requirements for BMI and medical conditions depend on the payor. If
            insurance is paying, these are the requirements:
          </p>

          <h3 className="text-xl md:text-[1.25rem] font-serif text-brand-mid text-center pt-1">
            If insurance is paying
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">Your BMI</th>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">Medical conditions necessary?</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">40 or higher</td>
                  <td className="border border-border px-4 py-2">No.</td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">35 – 39.9</td>
                  <td className="border border-border px-4 py-2">Yes. Without a qualifying diagnosis, most plans will decline.</td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">30 – 34.9</td>
                  <td className="border border-border px-4 py-2">Yes, but most plans do not cover surgery at this BMI at all. A rare few will cover it, if and only if type 2 diabetes is present.</td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">Under 30</td>
                  <td className="border border-border px-4 py-2">Not a candidate for bariatric surgery.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl md:text-[1.25rem] font-serif text-brand-mid text-center pt-1">
            If you are self-paying
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">Your BMI</th>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">Medical conditions necessary?</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">40 or higher</td>
                  <td className="border border-border px-4 py-2">No.</td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">35 – 39.9</td>
                  <td className="border border-border px-4 py-2">No.</td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">30 – 34.9</td>
                  <td className="border border-border px-4 py-2">Yes. You are a candidate if you have medical disease.</td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">Under 30</td>
                  <td className="border border-border px-4 py-2">Not a candidate for bariatric surgery.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            If the two tables look different to you at the same BMI, you are reading
            them correctly. The reason is explained below.
          </p>

          <h3 id="which-conditions-count" className="scroll-mt-24 text-xl md:text-[1.25rem] font-serif text-brand-mid text-center pt-1">
            Which conditions count
          </h3>
          <p>
            If your BMI is 35 to 39.9, your insurance company will not cover surgery
            unless you have a qualifying obesity-related condition. Your insurer
            decides which conditions are qualifying. The rules may differ from
            insurer to insurer, but some things are constant. For instance, there
            are four conditions that always qualify you for surgery:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Type 2 diabetes</li>
            <li>High blood pressure</li>
            <li>Obstructive sleep apnea</li>
            <li>Coronary artery disease or other heart disease</li>
          </ul>
          <p>
            There are additional obesity-related health issues that <em>may</em>{" "}
            qualify you, but they may not. It depends on your insurance. They are:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>High cholesterol or other lipid disorder</li>
            <li>Severe osteoarthritis or weight-bearing joint disease</li>
            <li>Fatty liver disease</li>
            <li>Severe gastroesophageal reflux</li>
            <li>Pulmonary hypertension or obesity hypoventilation syndrome</li>
          </ul>
          <p>
            Whether the above five conditions qualify you depends on your insurer.
            An easy way to get the answer is by checking their medical policy
            online. Just google,{" "}
            "Medical policy for weight loss surgery for [my insurance company]".
            Another easy way to check is by calling us. We'll find out for you.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              search={{ topic: "insurance" }}
              className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Request Bariatric Coverage Check
            </Link>
            <Link
              to="/appointments"
              className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
            >
              Schedule a Consultation
            </Link>
          </div>
          <aside
            aria-labelledby="qualify-without-knowing"
            className="mt-6 rounded-xl border border-brand bg-brand-soft p-5 md:p-6"
          >
            <h3 id="qualify-without-knowing" className="text-xl md:text-[1.25rem] font-serif text-brand-deep text-center mb-2">
              You may qualify without knowing it!
            </h3>
            <p className="text-brand-deep">
              Do not assume that you don’t have a qualifying condition. A qualifying
              diagnosis is often just waiting there for somebody to look for it.
            </p>
            <p className="mt-3 text-brand-deep">
              For example, studies have shown that over 70% of people whose BMI is
              between 35 and 39.9 have obstructive sleep apnea.<sup>1</sup> If you have
              never been evaluated for it, it is wise to check. You may already
              medically qualify without knowing it.
            </p>
          </aside>

          <h2 id="why-thresholds-differ" className="scroll-mt-24 text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            Why the BMI thresholds are different if you have insurance
          </h2>
          <p>
            The explanation is pretty straightforward. Most insurance criteria
            are based on a National Institutes of Health consensus statement
            published in 1991 — BMI 40 and above, or 35 and above with a
            comorbidity. Those thresholds have been embedded in payor policy for
            more than thirty years.
          </p>
          <p>
            Medical science has moved on. In 2022, the American Society for
            Metabolic and Bariatric Surgery and the International Federation for the
            Surgery of Obesity issued updated indications recommending surgery for
            anyone with a BMI of 35 or higher regardless of whether they have a
            related condition, and stating that surgery should be considered for
            people with metabolic disease at a BMI of 30 to 34.9.
          </p>
          <p>
            When you are self-paying, current clinical guidelines govern. When
            insurance is paying, we have to go by your plan’s medical policy.
            Sadly, most plans still use the older thresholds.
          </p>

          <h2 className="text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            Two caveats worth knowing
          </h2>

          <h3 id="east-asian" className="scroll-mt-24 text-xl md:text-[1.25rem] font-serif text-brand-mid text-center pt-1">
            1. If you are of East Asian descent, the thresholds are lower
          </h3>
          <p>
            Body mass index does not carry the same metabolic risk across all
            populations. At any given BMI, people of East Asian descent tend to have
            more visceral fat and a higher risk of type 2 diabetes and
            cardiovascular disease than people of European descent at the same
            number.
          </p>
          <p>
            The 2022 ASMBS and IFSO guidelines account for this directly,
            recommending that BMI risk zones be adjusted to define obesity at a
            threshold of 25 to 27.5 in this population, and stating that access to
            surgery should not be denied solely on the basis of traditional BMI
            thresholds.
          </p>
          <p>
            Many insurers' medical policies recognize an Asian-heritage
            adjustment, but some do not. Checking is easy, and it should happen
            early to avoid a disappointing surprise later on.
          </p>

          <h3 id="endoscopic" className="scroll-mt-24 text-xl md:text-[1.25rem] font-serif text-brand-mid text-center pt-1">
            2. The transoral procedures have their own BMI windows — including a ceiling
          </h3>
          <p>
            Sleeve gastrectomy and gastric bypass have a floor but no upper limit.
            The endoscopic procedures, performed through the mouth with no
            incisions, work differently: they are approved for defined BMI ranges
            with both a lower and an upper bound. Above the ceiling, they are not
            the right tool.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">Procedure</th>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">BMI window</th>
                  <th className="border border-border bg-muted/40 px-4 py-2 font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">
                    <Link to="/services/esg" className="text-brand-mid underline">
                      Endoscopic sleeve gastroplasty (ESG)
                    </Link>
                  </td>
                  <td className="border border-border px-4 py-2">30 – 50</td>
                  <td className="border border-border px-4 py-2">
                    FDA authorization specifies BMI 30–50 in adults who have not been
                    able to lose weight or maintain weight loss through more
                    conservative measures. Almost always self-pay.
                  </td>
                </tr>
                <tr>
                  <td className="border border-border px-4 py-2 font-semibold">
                    <Link to="/services/balloon" className="text-brand-mid underline">
                      Gastric balloon (Orbera)
                    </Link>
                  </td>
                  <td className="border border-border px-4 py-2">30 – 40</td>
                  <td className="border border-border px-4 py-2">
                    FDA approval specifies BMI 30–40, prior conservative weight-loss
                    attempts, and participation in a supervised diet and behavior
                    program. The balloon stays in place for six months. Almost always
                    self-pay.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Both endoscopic options are generally not covered by insurance, which
            means the self-pay table is the relevant one for them. They are worth
            discussing if your BMI falls in the 30 to 40 range, where surgical
            options may be limited by coverage and the endoscopic options are
            squarely on label.
          </p>

          <h2 id="what-to-do-next" className="scroll-mt-24 text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            What to do next
          </h2>
          <ol className="list-decimal pl-6 space-y-1">
            <li>
              <strong>Calculate your BMI.</strong> It takes ten seconds and tells you
              which row you are in.
            </li>
            <li>
              <strong>Find out whether you have a qualifying condition.</strong> If
              you have not been evaluated for sleep apnea, diabetes, or fatty liver,
              you may have one and not know it.
            </li>
            <li>
              <strong>Call your insurer</strong> and ask two questions: does my plan
              include a bariatric surgery benefit, and what BMI and documentation
              does it require. If the benefit does not exist, that changes the whole
              conversation early rather than late. Our{" "}
              <Link to="/resources/insurance-coverage" className="text-brand-mid underline">
                insurance coverage guide
              </Link>{" "}
              has plan-specific details for{" "}
              <Link to="/medical-weight-loss/insurance" className="text-brand-mid underline">
                Virginia
              </Link>{" "}
              and{" "}
              <Link to="/medical-weight-loss/insurance/nc" className="text-brand-mid underline">
                North Carolina
              </Link>
              , but other states are coming soon. Over 1,000 patients have traveled
              to us from Maryland, Delaware, and West Virginia, but some travel
              further. (Patients have traveled from Canada, Europe, and the Middle
              East to have surgery with Dr. Fitzer.)
            </li>
            <li>
              <strong>Come in for a consultation.</strong> We will confirm where you
              stand, order what is missing, and give you a straight answer about
              which options are actually open to you.
            </li>
          </ol>

          <section
            aria-labelledby="where-you-stand"
            className="rounded-xl border border-border bg-muted/40 p-5 md:p-6 text-center"
          >
            <h2 id="where-you-stand" className="text-2xl md:text-[1.6rem] font-serif text-brand-mid mb-3">
              Find out where you stand
            </h2>
            <p>
              Bring your insurance card and any records you have. If surgery is not
              the right answer for you, we will say so, and we will talk about what
              is.
            </p>
            <p className="mt-4">
              <Link
                to="/appointments"
                className="inline-block rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Request a consultation
              </Link>
            </p>
          </section>

          <p className="text-sm text-muted-foreground">
            This page is general information, not medical advice and not a guarantee
            of insurance benefits. Individual candidacy is determined through
            clinical evaluation, and coverage criteria vary by plan. Reviewed
            August 18, 2026.
          </p>

          <h2 id="sources" className="scroll-mt-24 text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            Sources
          </h2>
          <ol className="list-decimal pl-6 space-y-1.5 text-base">
            <li>
              Lopez PP, Stefan B, Schulman CI, Byers PM. Prevalence of sleep apnea in morbidly obese patients who presented for weight loss surgery evaluation: more evidence for routine screening for obstructive sleep apnea before weight loss surgery. <em>Am Surg.</em> 2008 Sep;74(9):834-8. PMID: 18807673.
            </li>
            <li>
              Eisenberg D, Shikora SA, Aarts E, et al. 2022 American Society for
              Metabolic and Bariatric Surgery (ASMBS) and International Federation
              for the Surgery of Obesity and Metabolic Disorders (IFSO): Indications
              for Metabolic and Bariatric Surgery. <em>Surg Obes Relat Dis.</em> 2022.
            </li>
            <li>
              NIH Consensus Development Conference, Gastrointestinal Surgery for
              Severe Obesity, 1991 — the source of the BMI 40 / BMI
              35-with-comorbidity thresholds still used by most payors.
            </li>
            <li>
              FDA, ORBERA Intragastric Balloon System, Summary of Safety and
              Effectiveness Data, P140008 (indications for use, BMI 30–40).
            </li>
            <li>
              FDA, De Novo classification DEN210045, Apollo ESG and Apollo ESG Sx
              Systems (indications for use, BMI 30–50).
            </li>
            <li>ASMBS, Endorsed Procedures and FDA-Approved Devices.</li>
          </ol>
        </article>

        <section aria-labelledby="faq" className="mt-8">
          <h2 id="faq" className="scroll-mt-24 text-2xl md:text-[1.6rem] font-serif text-brand-mid text-center pt-2 md:pt-3">
            Frequently asked questions
          </h2>
          <div className="mt-3">
            {FAQ_ITEMS.map((f, i) => (
              <details
                key={i}
                id={faqAnchorId(f.question)}
                className="scroll-mt-24 border-b border-border"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 py-4 text-left text-base md:text-[1.0625rem] font-medium hover:underline">
                  {f.question}
                </summary>
                <div className="pb-4 md:text-[1.125rem] leading-[1.6]">
                  {f.answer.split("\n\n").map((para, j) => (
                    <p key={j} className="mb-3 last:mb-0">{para}</p>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/appointments"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Request a Consultation
          </Link>
          <Link
            to="/resources/insurance-coverage"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Insurance Coverage Guide
          </Link>
        </div>
      <LastUpdated className="mt-10" />
      </PageSection>
    </>
  );
}
